import './payment.scss';

import { lazy, Suspense, useEffect, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import MoneyDisplay from '@/resources/components/global/MoneyDisplay/MoneyDisplay';
import { useCartContext } from '@/resources/hooks/useCartContext';
import { useOrderContext } from '@/resources/hooks/useOrderContext';
import { ApplicationState } from '@/store';
import * as PaymentActions from '@/store/ducks/payment/actions';

import {
  getFullAddress,
  getOrderMinValue,
  translatePaymentMethod,
} from '../../../../../services/global';
import { subscribeToPayment } from '../../../../../services/socket';
import { IAddress, IOrder } from '../../../../../store/ducks/order/types';
import OrderResume from '../../orderResume/OrderResume';
import Subtotal from '../../subtotal/Subtotal';
import DeliveryInformation from '../address/components/delivery/DeliveryInformation';
import RenderOrderAddress from '../address/components/RenderOrderAddress';
import { ShipmentCard } from '../shipmentCard/ShipmentCard';
import { PaymentButton } from './PaymentButton';

const PaymentPlansModal = lazy(() => import('./components/PaymentPlansModal'));

interface Props {
  order: IOrder;
  handleFinishOrder: () => boolean;
  forwardedOrderAlert: (alert: boolean) => void;
}

function Payment({ order, handleFinishOrder, forwardedOrderAlert }: Props) {
  const { useFunction } = useOrderContext();
  const { min_order_amount } = useCartContext();
  const { id: orderId, cart } = order;
  const isValidatedOrder = useSelector<ApplicationState, boolean>((state) => state.order.validated);
  const dispatch = useDispatch();

  const [show, setShow] = useState<boolean>(false);
  const [isPaid, setIsPaid] = useState<boolean>(false);

  const proceedToFinished = useFunction('proceedToFinished') || (() => {});

  const isAllItemsAvaliable = order.cart.products.every((item) => item.is_available === true);
  const validPayments = order?.payments
    ?.filter((p) => !p.deleted_at)
    .filter((p, index, arr) => {
      const next = arr[index + 1];
      const prev = arr[index - 1];

      const isSameAsNext = next && p.total_paid === next.total_paid;
      const prevSumExceeds = prev && p.total_paid + prev.total_paid > order?.total;

      return !(isSameAsNext && prevSumExceeds);
    });

  const canFinishUnpaid = validPayments?.every((p) => p.integration?.data?.can_finish_unpaid);

  useEffect(() => {
    if (orderId) {
      subscribeToPayment({
        orderId,
        callback: (order) => {
          if (order.is_paid) {
            setIsPaid(true);
            proceedToFinished();
            dispatch(PaymentActions.setShowPaidAlert(true));
          }
        },
      });
    }
  }, [orderId, isValidatedOrder]);

  const [validUniquePayment, setValidUniquePayment] = useState<boolean>(false);

  return (
    <>
      <ShipmentCard warningType="cliente" setShowModal={() => {}} showModal={false}>
        <ShipmentCard.Content isResume title="Resumo">
          <div className="w-100 resume">
            <div className="d-flex justify-content-between flex-row">
              <p className="text-left">Identificação: </p>
              <span className="text-right">{order.client.two_client_name}</span>
            </div>
            <div className="d-flex justify-content-between flex-row">
              <p className="text-left">
                {order.cart.products.length} {order.cart.products.length === 1 ? 'item' : 'itens'}{' '}
              </p>
              <span className="text-right">
                <MoneyDisplay value={order.cart.items_total} />
              </span>
            </div>
            {order.shipment.type === 'delivery' && (
              <>
                <div className="d-flex justify-content-between flex-row">
                  <p className="text-left">Endereço: </p>
                  <span
                    className="text-right text-truncate"
                    title={getFullAddress(order.shipment.address as IAddress, true)}
                    style={{ maxWidth: '30ch' }}
                  >
                    {getFullAddress(order.shipment.address as IAddress, true)}
                  </span>
                </div>
                <DeliveryInformation hideDeliveryOptions fromPayment />
              </>
            )}
            {order.shipment.type === 'shop' && (
              <RenderOrderAddress
                type={order.shipment.type}
                address={order.shipment.address}
                shopNameDst={order.shop_name_dst}
                pickupAt={order.shipment.pickup_at}
                fromPayment
              />
            )}
          </div>
        </ShipmentCard.Content>
      </ShipmentCard>

      <ShipmentCard
        showModal={show}
        setShowModal={setShow}
        warningType="pagamento"
        isPaid={
          validPayments &&
          validPayments.length > 0 &&
          validPayments.every((p) => p.status === 'paid')
        }
      >
        <ShipmentCard.Content
          title="3 - Pagamento"
          actions={
            !!order.payments?.length &&
            order.cart.items_total > getOrderMinValue(order, min_order_amount) &&
            !validUniquePayment
          }
          footer={
            <>
              <hr className="my-1" />
              <OrderResume order={order} />
              <Subtotal cart={cart} />

              {isAllItemsAvaliable && !validPayments?.some((p) => p.status === 'paid') && (
                <PaymentButton
                  setShow={setShow}
                  show={show}
                  isPaid={isPaid}
                  handleFinishOrder={handleFinishOrder}
                  setValidUniquePayment={setValidUniquePayment}
                  forwardedOrderAlert={forwardedOrderAlert}
                />
              )}

              {!isAllItemsAvaliable && (
                <p className="payment-availability">
                  *Não é possível finalizar um pedido com um produto indisponível, por gentileza,
                  remova-o do carrinho.
                </p>
              )}
            </>
          }
        >
          {validPayments && validPayments.length > 0 && (
            <div>
              {validPayments.map((payment) => {
                return (
                  <div
                    className="d-flex flex-column align-items-start payment-method"
                    key={payment.id}
                  >
                    <p className="mb-0">
                      <span>
                        <strong>Forma: </strong>
                        <span>{translatePaymentMethod(payment.payment_method)}</span>
                      </span>

                      {payment.status === 'paid' && (
                        <Badge variant="success" className="ml-1">
                          Pago
                        </Badge>
                      )}
                    </p>

                    <p className="mb-0">
                      <span>
                        <strong>Prazo: </strong>
                        <span>{payment.payment_plan_name}</span>
                      </span>
                    </p>

                    {!validPayments?.every((p) => p.status === 'paid') && (
                      <small className={`badge badge-${canFinishUnpaid ? 'secondary' : 'warning'}`}>
                        {canFinishUnpaid
                          ? '(pagamento efetuado na entrega)'
                          : 'aguardando pagamento'}
                      </small>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ShipmentCard.Content>

        <Suspense fallback={<div />}>
          <PaymentPlansModal show={show} setShow={setShow} />
        </Suspense>
      </ShipmentCard>
    </>
  );
}

export default Payment;
